/**
 * Quarterly Feedback — types for the `/api/feedback` contract.
 *
 * NOTE: these mirror the backend document for branch
 * `feature/Task-LI35-Refractor-Feedback-Functionality` exactly. Field names and
 * nullability are taken from the published response samples, so the UI never
 * has to guess a shape.
 *
 * This is a NEW module and does not touch the legacy `api/review` types used by
 * `pages/feedback/ReviewList.tsx`.
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/** SELF = the employee's own goal sheet. MANAGER = feedback written about them. */
export type FeedbackKind = "SELF" | "MANAGER";

/** MANAGER feedback is always SUBMITTED; only SELF moves through all three. */
export type FeedbackStatus = "DRAFT" | "SUBMITTED" | "APPROVED";

// ── Business rules the UI must enforce before calling the API ────────────────

/** At most 5 goal rows per sheet (server rejects a 6th with a 400). */
export const MAX_GOAL_ROWS = 5;

/** Weightage across rows must be exactly this to submit a self feedback. */
export const REQUIRED_TOTAL_WEIGHTAGE = 100;

/** Overall and per-row ratings are 1–5; both are optional everywhere. */
export const MIN_RATING = 1;
export const MAX_RATING = 5;

/**
 * Field length caps, so the form can stop the user before the server does.
 *
 * CHANGE: `MAX_KPI_LENGTH` removed with the `kpi` field. The contract now has a
 * SINGLE "KPI / achievement" box per row, which is `achievement`.
 */
export const MAX_ACHIEVEMENT_LENGTH = 2000;
export const MAX_AREA_NAME_LENGTH = 60;
export const MAX_OBJECTIVE_NAME_LENGTH = 150;

// ── KRA admin config ─────────────────────────────────────────────────────────

/** One objective inside an area. Seeded rows have `isDefault: true`. */
export interface KraObjective {
  id: number;
  areaId: number;
  name: string;
  sortOrder: number;
  isDefault: boolean;
  isActive: boolean;
}

/** A KRA area (Project, Self Development, …) with its objectives. */
export interface KraArea {
  id: number;
  name: string;
  sortOrder: number;
  isDefault: boolean;
  isActive: boolean;
  objectives: KraObjective[];
}

/** POST / PUT body for an area or an objective (all optional on PUT). */
export interface KraWriteRequest {
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
}

// ── Goal-sheet rows ──────────────────────────────────────────────────────────

/**
 * A row as SENT to the server.
 *
 * NOTE: rows are REPLACED on every save, so the full list goes up each time and
 * row ids change. The form therefore keys rows by a local uid, not by `id`.
 */
export interface FeedbackItemRequest {
  areaId: number;
  objectiveId: number;
  weightage?: number | null;
  /**
   * The one "KPI / achievement" box, typed by the user (max 2000).
   *
   * CHANGE: the separate `kpi` field is gone from the contract. On a SELF sheet
   * this is required on every row to submit; on manager feedback rows it is the
   * author's comment for that goal and stays optional.
   */
  achievement?: string | null;
  rating?: number | null;
}

/**
 * A row as RETURNED by the server. The area and objective names are copied onto
 * the row at save time, so a later rename never rewrites saved feedback — which
 * is why the viewer renders `areaName`/`objectiveName` rather than looking the
 * ids up in the current config.
 */
export interface FeedbackItem {
  id: number;
  areaId: number;
  areaName: string;
  objectiveId: number;
  objectiveName: string;
  weightage: number | null;
  /** The row's "KPI / achievement" text. CHANGE: replaces the old `kpi` + `achievement` pair. */
  achievement: string | null;
  rating: number | null;
}

// ── Feedback ─────────────────────────────────────────────────────────────────

/** One feedback record, self or manager. Shape is identical for both kinds. */
export interface Feedback {
  id: number;
  kind: FeedbackKind;

  /** Who the feedback is ABOUT. */
  userId: number;
  userName: string;
  userEmail: string;

  /** Who WROTE it (the employee themselves on a SELF record). */
  authorId: number;
  authorName: string;

  year: number;
  quarter: number;
  /** Pre-formatted label, e.g. "Q4 2026" — render this rather than rebuilding it. */
  period: string;

  status: FeedbackStatus;
  overallComment: string | null;
  overallRating: number | null;

  items: FeedbackItem[];
  /**
   * 0 when no row carries a weightage.
   *
   * CHANGE: no longer "always 0 on approving feedback" — rows are allowed there
   * now, so an approving record reports the APPROVER's own total.
   */
  totalWeightage: number;

  /** SELF: when submitted (null while DRAFT). MANAGER: when written. */
  submittedAt: string | null;

  /** SELF + APPROVED only. */
  approvedBy: number | null;
  approvedByName: string | null;
  approvedAt: string | null;
  /**
   * SELF + APPROVED: id of the manager feedback holding the reviewer's comment,
   * but ONLY when this viewer may open it (an admin, the approver, or the
   * employee once the approver shared it).
   *
   * NOTE: null does NOT mean the sheet is unapproved — it usually means the
   * reviewer kept their comment private. Drive the "Approved by …" line off
   * `status`/`approvedByName`, never off this.
   */
  approvalFeedbackId: number | null;

  /** MANAGER: the self feedback this approved; null for standalone feedback. */
  selfFeedbackId: number | null;

  /**
   * MANAGER: whether the employee can see this feedback. `null` on SELF.
   *
   * CHANGE: new in the contract. Manager feedback is PRIVATE by default — the
   * employee sees nothing until the author shares it. The approval itself
   * (status, approver, time) is always visible; only the comment, rating and
   * rows follow this flag.
   */
  visibleToEmployee: boolean | null;

  createdAt: string;
  updatedAt: string;

  /**
   * Whether THIS viewer may edit it right now.
   *
   * NOTE: the contract is explicit that the UI must use this flag for the Edit
   * button rather than re-deriving it from status and role — the server already
   * accounts for authorship, role changes and the approval link.
   */
  editable: boolean;
}

// ── Request bodies ───────────────────────────────────────────────────────────

/**
 * POST /self and PUT /self/{id}.
 *
 * `year` + `quarter` go together or not at all (omitted = current quarter), and
 * only the current or previous quarter is accepted.
 */
export interface SelfFeedbackRequest {
  year?: number;
  quarter?: number;
  overallComment?: string | null;
  overallRating?: number | null;
  items: FeedbackItemRequest[];
  /**
   * false saves a draft; true validates and locks it. To submit, every row needs
   * a weightage AND a "KPI / achievement", and the weightages must total 100.
   */
  submit: boolean;
}

/**
 * POST /employee and PUT /employee/{id}.
 *
 * Two distinct uses, which is why most fields are optional:
 *  - standalone feedback → `userId` OR `email`, plus optional year/quarter/items
 *  - approving a sheet   → `selfFeedbackId`, plus OPTIONAL rows (CHANGE: rows
 *    used to be forbidden here). Employee and quarter come from the sheet.
 *
 * On PUT the server IGNORES `userId`, `email` and `selfFeedbackId`.
 */
export interface ManagerFeedbackRequest {
  userId?: number;
  email?: string;
  year?: number;
  quarter?: number;
  selfFeedbackId?: number;
  overallComment: string;
  overallRating?: number | null;
  items?: FeedbackItemRequest[];
  /**
   * `true` shares it with the employee (and notifies them), `false` keeps it
   * private. CHANGE: new. On PUT, LEAVING IT OUT keeps the current value — which
   * is what makes a "Share with employee" toggle a plain PUT.
   */
  visibleToEmployee?: boolean;
}

// ── Read models ──────────────────────────────────────────────────────────────

/** One row of the manager/admin summary list, and the header of a user screen. */
export interface FeedbackSummary {
  userId: number;
  /** The employee CODE (a string), not the numeric id. Null until codes exist. */
  employeeId: string | null;
  name: string;
  email: string;
  designation: string | null;
  year: number;
  selfCount: number;
  receivedCount: number;
  /** Self feedback waiting for approval — the admin's action signal. */
  pendingReviewCount: number;
  /** Filled only for the current year. */
  currentPeriod: string | null;
  /** null = the employee has not started this quarter's sheet. */
  currentPeriodStatus: FeedbackStatus | null;
  lastFeedbackAt: string | null;
}

/** The manager or admin who wrote a group of feedback. */
export interface FeedbackAuthorRef {
  id: number;
  name: string;
  email: string;
  designation: string | null;
}

/**
 * CHANGE: `received` is no longer a flat list — the server now groups it by the
 * person who wrote it, so a reader can see at a glance WHO has given this
 * employee feedback and HOW MUCH each of them gave. That question was
 * unanswerable from a flat list without scanning every card's author line.
 */
export interface ReceivedFeedbackGroup {
  /** Named `manager` by the contract; an admin author arrives here too. */
  manager: FeedbackAuthorRef;
  /**
   * The server's own count for this author. Treated as a display hint only —
   * `feedback.length` is what the UI renders, so a stale or filtered count can
   * never disagree with the cards actually on screen.
   */
  count: number;
  feedback: Feedback[];
}

/** GET /user — one employee's whole screen for one year. */
export interface UserFeedbackResponse {
  summary: FeedbackSummary;
  /** Year dropdown, newest first; always includes the current year. */
  years: number[];
  /** Self tab. Empty when `kind=MANAGER` was requested. */
  self: Feedback[];
  /** Manager tab, grouped by author. Empty when `kind=SELF` was requested. */
  received: ReceivedFeedbackGroup[];
}

/**
 * Normalises whatever `received` actually arrives into author groups.
 *
 * NOTE: tolerates the PREVIOUS flat `Feedback[]` shape as well as the grouped
 * one. The two deploy independently, and a front end that throws on the old
 * payload turns a brief version skew into a blank screen. A flat array is
 * bucketed by `authorId`, which reconstructs exactly the grouping the server
 * now does for us.
 */
export const toReceivedGroups = (received: unknown): ReceivedFeedbackGroup[] => {
  if (!Array.isArray(received) || received.length === 0) return [];

  // Grouped shape: every entry carries a `feedback` array.
  const isGrouped = received.every(
    (entry) => entry && typeof entry === "object" && Array.isArray((entry as ReceivedFeedbackGroup).feedback),
  );
  if (isGrouped) return received as ReceivedFeedbackGroup[];

  // Legacy flat shape — rebuild the groups from each record's author.
  const buckets = new Map<number, ReceivedFeedbackGroup>();
  (received as Feedback[]).forEach((item) => {
    if (!item) return;
    const existing = buckets.get(item.authorId);
    if (existing) {
      existing.feedback.push(item);
      existing.count = existing.feedback.length;
      return;
    }
    buckets.set(item.authorId, {
      manager: {
        id: item.authorId,
        name: item.authorName,
        // The flat shape carried neither of these on the author.
        email: "",
        designation: null,
      },
      count: 1,
      feedback: [item],
    });
  });
  return Array.from(buckets.values());
};

/** Every received record across all authors — for lookups and totals. */
export const flattenReceived = (groups: ReceivedFeedbackGroup[]): Feedback[] =>
  groups.flatMap((group) => group.feedback ?? []);

// ── Query parameter types ────────────────────────────────────────────────────

/**
 * Server-side pagination + search, shared by every paged list endpoint.
 *
 * NOTE: kept generic (no feedback-specific fields) so other services can extend
 * it instead of re-declaring page/size/search inline at each call site.
 */
export interface PagedQuery {
  page?: number;
  size?: number;
  search?: string;
}

/** Default page size for the summary list; the server caps `size` at MAX_PAGE_SIZE. */
export const DEFAULT_PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 100;

/** GET /summary — one row per employee, for a single year. */
export interface FeedbackSummaryQuery extends PagedQuery {
  year?: number;
}

/**
 * GET /user — one employee's screen.
 * Omit both `userId` and `email` to load your own screen.
 */
export interface UserFeedbackQuery {
  userId?: number;
  email?: string;
  year?: number;
  /** Omit to fill both lists (Overview); pass one to fill only that tab. */
  kind?: FeedbackKind;
}

/**
 * Approving a submitted sheet.
 *
 * NOTE: a named type rather than positional arguments, because the two string
 * and number fields are easy to transpose at a call site and the server would
 * accept the mistake silently.
 */
export interface ApproveFeedbackRequest {
  selfFeedbackId: number;
  overallComment: string;
  overallRating?: number | null;
  /**
   * CHANGE: approving feedback may now carry goal rows. The UI prefills the
   * employee's own rows so the reviewer can score each goal and add a comment
   * per goal, rather than only writing one overall paragraph.
   */
  items?: FeedbackItemRequest[];
  /** Default false: the reviewer's comment stays private unless shared. */
  visibleToEmployee?: boolean;
}

/** Spring page wrapper for GET /summary. */
export interface FeedbackSummaryPage {
  content: FeedbackSummary[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  empty: boolean;
}

// ── Quarter helpers ──────────────────────────────────────────────────────────

/**
 * Quarters are CALENDAR quarters in IST. The browser may sit in any timezone,
 * so the current quarter is derived from the IST wall-clock date rather than
 * from the local one — otherwise a user in a westward zone could be offered
 * the wrong quarter near a quarter boundary and get a 400 back.
 */
export const getIstNow = (): Date => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  // Month is 1-based from Intl; the Date constructor wants 0-based.
  return new Date(get("year"), get("month") - 1, get("day"));
};

export interface QuarterRef {
  year: number;
  quarter: number;
}

/** The calendar quarter (1–4) containing today, in IST. */
export const getCurrentQuarter = (): QuarterRef => {
  const now = getIstNow();
  return { year: now.getFullYear(), quarter: Math.floor(now.getMonth() / 3) + 1 };
};

/** The quarter before `ref`, rolling the year back across the Q1 boundary. */
export const getPreviousQuarter = (ref: QuarterRef): QuarterRef =>
  ref.quarter === 1
    ? { year: ref.year - 1, quarter: 4 }
    : { year: ref.year, quarter: ref.quarter - 1 };

/** "Q4 2026" — matches the server's own `period` spelling. */
export const formatPeriod = (ref: QuarterRef): string => `Q${ref.quarter} ${ref.year}`;

/**
 * The months each calendar quarter covers.
 *
 * CHANGE: new. "Q4 2026" is unambiguous to whoever set the module up and
 * opaque to everybody else — people were reading the quarter labels without
 * knowing which months they stood for. The range is shown wherever a quarter
 * is CHOSEN or READ, so nobody has to remember the mapping.
 *
 * NOTE: these are calendar quarters, matching getCurrentQuarter's
 * `Math.floor(month / 3) + 1`. If the company ever moves to a fiscal year
 * (Apr-Mar is common in India), this table and that line must change together
 * — they are the only two places the quarter boundaries are expressed.
 */
export const QUARTER_MONTHS: Record<number, string> = {
  1: "Jan–Mar",
  2: "Apr–Jun",
  3: "Jul–Sep",
  4: "Oct–Dec",
};

/** "Oct–Dec" for 4. Empty for anything outside 1-4, so a bad value prints nothing. */
export const getQuarterMonths = (quarter: number): string => QUARTER_MONTHS[quarter] ?? "";

/**
 * "Q4 2026 · Oct–Dec" — for a single-string context such as a <option>, where
 * no second element can carry the hint.
 */
export const formatPeriodWithMonths = (ref: QuarterRef): string => {
  const months = getQuarterMonths(ref.quarter);
  return months ? `${formatPeriod(ref)} · ${months}` : formatPeriod(ref);
};

/** The only two quarters a self feedback may be written for, newest first. */
export const getWritableQuarters = (): QuarterRef[] => {
  const current = getCurrentQuarter();
  return [current, getPreviousQuarter(current)];
};

/**
 * The quarters a MANAGER may write feedback for, newest first.
 *
 * NOTE: the server allows any quarter up to the current one; an unbounded list
 * is useless in a dropdown, so this offers a recent window. `count` is the
 * number of quarters including the current one.
 */
export const getSelectableQuarters = (count = 4): QuarterRef[] => {
  const quarters: QuarterRef[] = [getCurrentQuarter()];
  for (let i = 1; i < count; i += 1) {
    quarters.push(getPreviousQuarter(quarters[i - 1]));
  }
  return quarters;
};

/** Stable "2026-4" key for a quarter, for select values and React keys. */
export const quarterKey = (ref: QuarterRef): string => `${ref.year}-${ref.quarter}`;

/** Parses a `quarterKey` back into a QuarterRef; null when malformed. */
export const parseQuarterKey = (value: string): QuarterRef | null => {
  const match = /^(\d{4})-([1-4])$/.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), quarter: Number(match[2]) };
};

/**
 * Formats an API date-time for display.
 *
 * NOTE: the API sends IST wall-clock with NO zone ("2026-10-30T10:15:42"), so
 * it must NOT go through `convertUtcToIst` or `new Date(...)` timezone logic —
 * that would shift an already-local time. The string is split literally.
 */
export const formatFeedbackDateTime = (value?: string | null): string => {
  if (!value) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return value;
  const [, year, month, day, hour, minute] = match;
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${day} ${MONTHS[Number(month) - 1]} ${year}, ${hour}:${minute}`;
};

/** Date only, for table cells where the time adds nothing. */
export const formatFeedbackDate = (value?: string | null): string => {
  const full = formatFeedbackDateTime(value);
  return full === "—" ? full : full.split(",")[0];
};
